import React, { useState, useEffect } from 'react';
import { productAPI, adminAPI } from '../../api';
import AdminProductUpload from './AdminProductUpload';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Protected products that cannot be deleted or edited
  const protectedProducts = [
    'Power+ Care',
    'Lady Care',
    'Allergy Care',
    'Joint Care',
    'Liv Super Care',
    'Paiels Care',
    'Multivitamin Care',
    'Gastric Care',
    'Dant Care',
    'Active Power'
  ];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productAPI.getAllProducts({ limit: 100 });
      setProducts(response.data?.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedProducts = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.seedProducts();
      alert(response.data.message);
      fetchProducts();
    } catch (error) {
      console.error('Error seeding products:', error);
      alert('Failed to seed products');
    } finally {
      setLoading(false);
    }
  };

  const isProductProtected = (productName) => {
    return protectedProducts.includes(productName);
  };

  const handleEdit = (product) => {
    if (isProductProtected(product.name)) {
      alert('This product is protected and cannot be edited. Protected products: ' + protectedProducts.join(', '));
      return;
    }
    setEditingProduct(product);
    setShowEditModal(true);
  };

  const handleDelete = async (productId, productName) => {
    if (isProductProtected(productName)) {
      alert('This product is protected and cannot be deleted. Protected products: ' + protectedProducts.join(', '));
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${productName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      await productAPI.deleteProduct(productId);
      alert('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const response = await productAPI.updateProduct(editingProduct._id, editingProduct);
      alert('Product updated successfully');
      setShowEditModal(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Failed to update product: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E4572E] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] p-6 rounded-2xl">
      {/* Header */}
      <div className="flex justify-between items-center mb-10">
        <div>
          <h2 className="text-3xl font-bold text-[#0F2F3D]">
            Product Management
          </h2>
          <p className="text-gray-500 mt-1">
            Manage your product catalog efficiently
          </p>
        </div>
        <div className="flex gap-4">
          {/* <button
            onClick={handleSeedProducts}
            className="px-4 py-2 bg-gradient-to-r from-[#E4572E] to-[#F47C4D] text-white rounded-lg shadow hover:opacity-90 transition-all duration-300"
          >
            Seed Products
          </button> */}
          <button
            onClick={fetchProducts}
            className="px-4 py-2 border border-[#0F2F3D] text-[#0F2F3D] rounded-lg hover:bg-[#0F2F3D] hover:text-white transition-all duration-300"
          >
            Refresh
          </button>
        </div>
      </div>
      {/* Product Upload Form */}
      <AdminProductUpload onProductAdded={fetchProducts} />
      {/* Table Card */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0F2F3D] text-white">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold">SR. No</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Category</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Price</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Stock</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Created</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {products.length > 0 ? (
                products.map((product, index) => (
                  <tr
                    key={index}
                    className="hover:bg-[#E4572E]/5 transition-all"
                  >
                    <td className="px-6 py-4 font-medium text-[#0F2F3D]">{index + 1}</td>
                    <td className="px-6 py-4 font-medium text-[#0F2F3D]">
                      {product.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {product.category}
                    </td>
                    <td className="px-6 py-4 font-bold text-[#E4572E]">
                      ₹{product.price}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {product.stock}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          product.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {product.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(product.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(product)}
                          className="text-blue-600 hover:text-blue-900 font-medium"
                          disabled={isProductProtected(product.name)}
                          title={isProductProtected(product.name) ? 'Protected product' : 'Edit product'}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(product._id, product.name)}
                          className="text-red-600 hover:text-red-900 font-medium"
                          disabled={isProductProtected(product.name)}
                          title={isProductProtected(product.name) ? 'Protected product' : 'Delete product'}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="text-center py-10 text-gray-500"
                  >
                    No products found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Product Modal */}
      {showEditModal && editingProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-xl font-bold text-[#0F2F3D] mb-4">Edit Product</h3>
            <form onSubmit={handleUpdateProduct}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                  <input
                    type="text"
                    value={editingProduct.name}
                    onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E4572E]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={editingProduct.category}
                    onChange={(e) => setEditingProduct({...editingProduct, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E4572E]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹)</label>
                  <input
                    type="number"
                    value={editingProduct.price}
                    onChange={(e) => setEditingProduct({...editingProduct, price: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E4572E]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
                  <input
                    type="number"
                    value={editingProduct.stock}
                    onChange={(e) => setEditingProduct({...editingProduct, stock: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E4572E]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editingProduct.isActive ? 'true' : 'false'}
                    onChange={(e) => setEditingProduct({...editingProduct, isActive: e.target.value === 'true'})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E4572E]"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingProduct(null);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-[#E4572E] text-white rounded-md hover:bg-[#F47C4D] disabled:opacity-50"
                  >
                    {loading ? 'Updating...' : 'Update Product'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;